import { parseArenaColliders, type ArenaColliders } from '@steelwake/shared/arena';
import type { Group, Mesh, Object3D } from 'three/webgpu';

import { disposeObject, setAnisotropy } from './dispose.js';
import type { Loaders } from './loaders.js';
import { assetUrl } from './paths.js';

export interface LoadedArena {
  root: Group;
  colliders: ArenaColliders;
  dispose(): void;
}

/** The arena's sidecar alone (small): the sky it names is loaded before the model. */
export async function fetchArenaColliders(id: string, signal: AbortSignal): Promise<ArenaColliders> {
  const res = await fetch(assetUrl(`arenas/${id}/${id}.colliders.json`), { signal });
  if (!res.ok) throw new Error(`arena colliders: HTTP ${String(res.status)}`);
  return parseArenaColliders(await (res.json() as Promise<unknown>));
}

export async function loadArena(
  loaders: Loaders,
  id: string,
  signal: AbortSignal,
  anisotropy: number,
  prefetched?: ArenaColliders,
  onProgress?: (event: ProgressEvent) => void,
): Promise<LoadedArena> {
  const [gltf, colliders] = await Promise.all([
    loaders.gltf.loadAsync(assetUrl(`arenas/${id}/${id}.glb`), onProgress),
    prefetched ? Promise.resolve(prefetched) : fetchArenaColliders(id, signal),
  ]);
  if (signal.aborted) {
    disposeObject(gltf.scene);
    signal.throwIfAborted();
  }
  gltf.scene.traverse((o: Object3D) => {
    if ((o as Mesh).isMesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  setAnisotropy(gltf.scene, anisotropy);
  return {
    root: gltf.scene,
    colliders,
    dispose: () => disposeObject(gltf.scene),
  };
}
